import { FormWrapperLabel, FormWrapperMinimum } from '../FormEntryWrapper/FormEntryWrapper';
import React, { useEffect, useState } from 'react';
import ValidationPatterns from '../../../RegEx';
import { checkEmailAddressUnique } from '../../../../Api/UsersEndpoints';
import useDebounce from '../../../Hooks/useDebounce';
import { Control, Controller } from 'react-hook-form';

interface EmailControlType {
	control: Control<any>;
	name: string;
	validationMessage: string;
	label?: string;
	labelExtra?: string;
	defaultValue?: any;
	invalid?: boolean;
	onChange?: any;
	forwardBlocker?: any;
	setValue?: any;
	setError?: any;
	clearErrors?: any;
	checkUnique?: boolean;
	minimal?: boolean;
	required?: boolean;
}

const EmailControl = (props: EmailControlType) => {
	const [emailValue, setEmailValue] = useState<string>(props.defaultValue);
	const [emailValidStructure, setEmailValidStructure] = useState<boolean>(false);
	const [emailValidValue, setEmailValidValue] = useState<boolean>(false);

	const [lookupActive, setLookupActive] = useState<boolean>(false);

	const emailDebounceValue = useDebounce(emailValue, emailValidStructure ? 1000 : 0);

	const labelValue = props.label ?? 'E-mail Address';

	const HandlePatternMatch = (input: string) => {
		let value = input;

		setLookupActive(true);

		if (props.onChange) {
			props.onChange(value);
		}

		const patternMatched = ValidationPatterns.email.test(value);

		if (emailDebounceValue.debouncedValue === value && emailValue === value && emailValidValue) {
			setLookupActive(false);
			return true;
		}

		if (!value || value === '') {
			setLookupActive(false);
			setEmailValidValue(false);
		} else if (!patternMatched) {
			props.setError(props.name, { type: 'custom', message: 'E-mail address format is incorrect' });
			setLookupActive(false);
			setEmailValidValue(false);
		} else {
			props.clearErrors(props.name);
		}

		setEmailValue(value);
		setEmailValidStructure(patternMatched);
		setEmailValidValue(patternMatched);

		if (!props.checkUnique) {
			setLookupActive(false);
		}

		return patternMatched;
	};

	const HandleUsernameCheck = async (value: string) => {
		if (!emailValidStructure) return false;

		return checkEmailAddressUnique(value)
			.then((r) => {
				if (!r.isUnique) {
					props.setError(props.name, { type: 'custom', message: 'E-mail address is already in use' });
					setEmailValidValue(false);
				} else {
					props.clearErrors(props.name);
				}

				setEmailValidValue(r.isUnique);
				return r.isUnique;
			})
			.catch((e) => {
				props.setError(props.name, { type: 'custom', message: `Something went wrong ${e}` });
				setEmailValidValue(false);
				return false;
			})
			.finally(() => {
				setLookupActive(false);
			});
	};

	useEffect(() => {
		if (props.forwardBlocker) {
			props.forwardBlocker(lookupActive);
		}
	}, [lookupActive]);

	useEffect(() => {
		if (emailDebounceValue?.debouncedValue && emailDebounceValue.debouncedValue !== '' && props.checkUnique) {
			HandleUsernameCheck(emailDebounceValue.debouncedValue).then(() => null);
		}
	}, [emailDebounceValue.debouncedValue]);

	return (
		<Controller
			control={props.control}
			name={props.name}
			defaultValue={props.defaultValue}
			rules={{
				required: { value: props.required || false, message: `${labelValue} is required` },
				pattern: { value: ValidationPatterns.email, message: `${labelValue} Format is not valid` },
			}}
			render={({ field, fieldState }) => {
				if (props.minimal) {
					return (
						<FormWrapperMinimum
							invalid={fieldState.error !== undefined}
							validationMessage={fieldState.error?.message}
							isActive={lookupActive}
						>
							<input
								aria-invalid={fieldState.error !== undefined}
								type={'email'}
								defaultValue={props.defaultValue}
								onChange={(e) => {
									HandlePatternMatch(e.currentTarget.value);
									field.onChange(e.currentTarget.value);
								}}
							/>
						</FormWrapperMinimum>
					);
				}

				return (
					<FormWrapperLabel
						label={labelValue}
						labelExtra={props.labelExtra}
						invalid={fieldState.error !== undefined}
						validationMessage={fieldState.error?.message}
						isActive={lookupActive}
					>
						<input
							aria-invalid={fieldState.error !== undefined}
							type={'email'}
							defaultValue={props.defaultValue}
							onChange={(e) => {
								field.onChange(e.currentTarget.value);
								HandlePatternMatch(e.currentTarget.value);
							}}
						/>
					</FormWrapperLabel>
				);
			}}
		/>
	);
};

export default EmailControl;
